import { useMemo } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Shell } from '../components/Shell';
import { navForUser, type RoleCode, type ViewKey } from '../nav/navModel';
import { useAuth } from '../auth/AuthContext';
import { useToast } from '../lib/useToast';
import { Locations } from './Locations';
import { Settings } from './Settings';
import { Products } from './Products';
import { Placeholder } from './Placeholder';
import { Warehouse } from './Warehouse';
import { Pos } from './Pos';
import { ReceiveStore } from './ReceiveStore';
import { MasterDispatch, MasterEntry } from './Master';
import { Inventory } from './Inventory';
import { Audit } from './Audit';
import { Movements } from './Movements';
import { Dashboard } from './Dashboard';
import { Cash } from './Cash';
import { PurchaseRequests } from './PurchaseRequests';
import { PurchaseAuth } from './PurchaseAuth';
import { Supply } from './Supply';
import { Credits } from './Credits';
import { Reports } from './Reports';
import { Cleanup } from './Cleanup';
import { WarehouseSales } from './WarehouseSales';

const SCREENS: Partial<Record<ViewKey, React.ComponentType>> = {
  stores: Locations,
  config: Settings,
  products: Products,
  warehouse: Warehouse,
  warehouseSales: WarehouseSales,
  pos: Pos,
  receiveStore: ReceiveStore,
  masterEntry: MasterEntry,
  masterDispatch: MasterDispatch,
  inventory: Inventory,
  audit: Audit,
  movements: Movements,
  dashboard: Dashboard,
  cash: Cash,
  purchaseRequests: PurchaseRequests,
  purchaseAuth: PurchaseAuth,
  supply: Supply,
  credits: Credits,
  reports: Reports,
  cleanup: Cleanup,
};

/** Menú y encabezado del prototipo alrededor de la vista activa, según el usuario autenticado. */
export function AppLayout({ view }: { view: ViewKey }) {
  const { user, logout, setLanguage } = useAuth();
  const { showError } = useToast();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const nav = useMemo(() => {
    if (!user) return [];
    const canDistribute = user.locations.some((l) => l.can_distribute) && user.role.scoped;
    return navForUser(user.role.code as RoleCode, user.permissions, { canDistribute }, user.granted);
  }, [user]);

  if (!user) return null;
  if (!nav.some((n) => n.view === view)) return <Navigate to={`/app/${nav[0]?.view ?? 'dashboard'}`} replace />;

  const context = user.role.scoped ? user.locations.map((l) => l.name).join(' · ') : t(`roles.${user.role.code}`);
  const Screen = SCREENS[view] ?? Placeholder;

  return (
    <Shell
      userName={user.full_name}
      userContext={context}
      nav={nav}
      active={view}
      onSelect={(v) => navigate(`/app/${v}`)}
      onLanguage={(lang) => void setLanguage(lang).catch(showError)}
      onLogout={() => {
        void logout().then(() => navigate('/login'));
      }}
    >
      <Screen />
    </Shell>
  );
}
